import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import { API_CONFIG } from '../config/api';
import { resetProfileVerifyGuide } from '../api/taskManagerApi';

export interface User {
  id: string;
  email: string;
  name?: string;
  is_active: boolean;
  email_verified_at?: string;
  last_login_at?: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  refreshUser: () => Promise<void>; // 新增：用于与回调页面对接
  getProfileVerifyStatus: () => Promise<boolean>;
  requestProfileCode: () => Promise<{ success: boolean; message: string }>;
  verifyProfileCode: (code: string) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 计算认证基址：保持配置原样（支持绝对或相对路径）。相对路径将通过Vite代理到任务管理器。
  const rawBase = API_CONFIG.TASK_MANAGER_BASE;
  const resolvedBase = rawBase && rawBase.replace(/\/$/, '');
  const baseURL = resolvedBase.endsWith('/')
    ? `${resolvedBase}auth`
    : `${resolvedBase}/auth`;
  // 诊断日志（仅开发环境有效）
  // eslint-disable-next-line no-console
  console.log('Auth base resolved:', { rawBase, resolvedBase, baseURL });

  // 普通邮箱登录
  const login = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${baseURL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      console.log('收到响应:', response.status, response.statusText);
      
      // 先检查响应状态
      if (!response.ok) {
        const errorText = await response.text();
        console.error('响应错误:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          return { success: false, message: errorData.detail || '发送失败，请稍后重试' };
        } catch {
          return { success: false, message: `服务器错误: ${response.status}` };
        }
      }

      // 解析成功响应
      const data = await response.json();
      console.log('响应数据:', data);
      setUser(data.user || null);
      return { success: true, message: data.message || '登录成功' };
      
    } catch (error) {
      console.error('发送Magic Link失败，详细错误:', error);
      console.error('错误类型:', error instanceof TypeError ? 'TypeError' : typeof error);
      if (error instanceof Error) {
        console.error('错误消息:', error.message);
        console.error('错误堆栈:', error.stack);
      }
      return { success: false, message: '网络错误，请检查连接' };
    }
  };

  // 获取当前登录用户
  const getCurrentUser = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${baseURL}/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 用户注销
  const logout = async () => {
    try {
      await fetch(`${baseURL}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('注销请求失败:', error);
    } finally {
      setUser(null);
      // 清除「稍后再说」的忽略标记，避免影响下次登录
      resetProfileVerifyGuide();
      message.success('已成功注销');
    }
  };

  // 个人中心邮箱验证：查询当前是否已验证
  const getProfileVerifyStatus = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${baseURL}/profile/status`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = await response.json();
      return !!data.verified;
    } catch (error) {
      console.error('查询个人中心验证状态失败:', error);
      return false;
    }
  };

  // 个人中心邮箱验证：发送验证码
  const requestProfileCode = async (): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${baseURL}/profile/request-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, message: data.detail || '验证码发送失败' };
      }
      return { success: true, message: data.message || '验证码已发送' };
    } catch (error) {
      console.error('发送验证码失败:', error);
      return { success: false, message: '网络错误，请检查连接' };
    }
  };

  // 个人中心邮箱验证：校验验证码
  const verifyProfileCode = async (code: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${baseURL}/profile/verify-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, message: data.detail || '验证失败' };
      }
      return { success: true, message: data.message || '验证通过' };
    } catch (error) {
      console.error('验证码校验失败:', error);
      return { success: false, message: '网络错误，请检查连接' };
    }
  };

  // 初始化时检查登录状态
  useEffect(() => {
    getCurrentUser();
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    getCurrentUser,
    refreshUser: getCurrentUser, // 新增：别名，保持与回调页面的API兼容
    getProfileVerifyStatus,
    requestProfileCode,
    verifyProfileCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};